import { inject } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { doc, Firestore, FirestoreDataConverter, Timestamp } from '@fido/firebase/firestore';
import { createInjectionToken } from 'ngxtension/create-injection-token';
import { observeDoc } from '../../firebase/observe-doc';
import { injectUser } from './user';

export interface SmsConsent {
  phoneNumber: string;
  consentTextVersion: string;
  acceptedAt: Timestamp;
  acceptedIpHash: string | null;
  userAgent: string | null;
}

export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string | null;
  photoURL?: string | null;
  emailVerified: boolean;
  smsConsent?: SmsConsent;
}

export const userProfileConverter: FirestoreDataConverter<UserProfile> = {
  toFirestore: (profile: UserProfile) => profile,
  fromFirestore: (snapshot) => snapshot.data() as UserProfile,
};

/**
 * Reactive signal for the current user's Firestore profile document.
 * Returns undefined if no user is signed in or document doesn't exist yet.
 * Automatically updates when the Firestore document changes.
 */
export const [injectUserProfile, provideUserProfile] = createInjectionToken(() => {
  const firestore = inject(Firestore);
  const user = injectUser();

  return rxResource({
    params: () => user()?.uid,
    stream: ({ params: uid }) =>
      observeDoc(
        doc(firestore, `users/${uid}`).withConverter(userProfileConverter),
        'User Profile',
      ),
  });
});
